import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '~/routes/components/AuthProvider'
import { DateInput } from 'semantic-ui-calendar-react'
import { handleErrors, parseDate } from '~/utils/helpers'
import Dialog from '~/components/Dialog'
import { DownloadService } from '~/api/services/downloadFiles'
import notify from '~/components/Notify'
import { KeyLabelType } from '~/types'
import { Form } from 'semantic-ui-react'

interface Props {
  openExport: boolean
  handleClose: () => void
  exportTypes: KeyLabelType[]
  months: KeyLabelType[]
}

const DAILY_TYPE_KEY = 'daily'
const INDIVIDUAL_TYPE_KEY = 'individual'

const Export: React.FC<Props> = (props) => {
  const { t } = useTranslation()
  const { openExport, handleClose, exportTypes, months } = props
  const { systemData: { languages, logged: { languageId } } } = useAuth()
  const languageLocal = languages.find(language => language.id === languageId)

  const [dateExport, setDateExport] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [type, setType] = useState('')
  const [isBusy, setIsBusy] = useState(false)

  useEffect(() => {
    if (openExport && exportTypes?.length > 0) {
      setType(exportTypes[0].key)
      setDateExport('')
      setDateFrom('')
      setDateTo('')
    }
  }, [openExport, exportTypes])

  const handleKeyPress = (event) => {
    event.preventDefault();
  }

  async function handleSubmit() {
    setIsBusy(true)
    const payload = type === INDIVIDUAL_TYPE_KEY
      ? { dateFrom: parseDate(dateFrom), dateTo: parseDate(dateTo), type }
      : { date: type === DAILY_TYPE_KEY ? parseDate(dateExport) : dateExport, type }
    try {
      await DownloadService.downloadFileWithName('milk-money/tours/export', payload)
      notify(t('Successfully downloaded tour file'), 'success')
      setDateExport('')
      handleClose()
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  function renderDatePicker() {
    return <Form.Field>
      <label>{t('Date')}</label>
      <DateInput
        name='date'
        autoComplete='off'
        placeholder={t('Select date')}
        closable
        clearIcon={'close'}
        clearable
        localization={languageLocal.code}
        onKeyPress={handleKeyPress}
        closeOnMouseLeave={false}
        dateFormat={'DD.MM.YYYY'}
        className={'datePicker'}
        value={dateExport}
        iconPosition='left'
        onChange={(e, data) => setDateExport(data.value)}
      />
    </Form.Field>
  }

  function renderMonthPicker() {
    return <Form.Select
      label={t('Date')}
      options={months?.map(month => ({ value: month.key, text: month.label }))}
      placeholder={t('Select date')}
      selectOnBlur={false}
      onChange={(_, { value }) => setDateExport(value as string)}
    />
  }

  function renderIndividualDatePickers() {
    return <>
      <Form.Field>
        <label>{t('Date From')}</label>
        <DateInput
          name='dateFrom'
          autoComplete='off'
          placeholder='DD.MM.YYYY'
          closable
          clearIcon={'close'}
          clearable
          localization={languageLocal.code}
          onKeyPress={handleKeyPress}
          closeOnMouseLeave={false}
          dateFormat={'DD.MM.YYYY'}
          className={'datePicker'}
          value={dateFrom}
          iconPosition='left'
          onChange={(e, data) => {
            setDateFrom(data.value)
            setDateTo('')
          }}
        />
      </Form.Field>
      <Form.Field>
        <label>{t('Date To')}</label>
        <DateInput
          name='dateTo'
          autoComplete='off'
          placeholder='DD.MM.YYYY'
          closable
          clearIcon={'close'}
          clearable
          localization={languageLocal.code}
          onKeyPress={handleKeyPress}
          closeOnMouseLeave={false}
          dateFormat={'DD.MM.YYYY'}
          className={'datePicker'}
          value={dateTo}
          iconPosition='left'
          disabled={dateFrom === ''}
          initialDate={dateFrom || undefined}
          minDate={dateFrom || undefined}
          onChange={(e, data) => setDateTo(data.value)}
        />
      </Form.Field>
    </>
  }

  function isSubmitDisabled() {
    if (type === '') {
      return true
    }

    switch (type) {
      case INDIVIDUAL_TYPE_KEY:
        return dateFrom === '' || dateTo === ''
      default:
        return dateExport === ''
    }
  }

  function renderFieldsByType() {
    switch (type) {
      case INDIVIDUAL_TYPE_KEY:
        return renderIndividualDatePickers()
      case DAILY_TYPE_KEY:
        return renderDatePicker()
      default:
        return renderMonthPicker()
    }
  }

  function renderFields() {
    return <Form>
      <Form.Group widths={'equal'}>
        <Form.Select
          label={t('Type')}
          options={exportTypes?.map(exportType => ({ value: exportType.key, text: exportType.label }))}
          placeholder={t('Type')}
          selectOnBlur={false}
          onChange={(_, { value }) => {
            setType(value as string)
            setDateExport('')
            setDateFrom('')
            setDateTo('')
          }}
        />
        {renderFieldsByType()}
      </Form.Group>
    </Form>
  }

  return <Dialog
    textHeader={t('Export tour file')}
    isLoading={isBusy}
    isDialogOpen={openExport}
    textSubmit={t('Export')}
    textClose={t('Close')}
    htmlContent={true}
    textContent={<>
      {renderFields()}
    </>}
    handleClose={() => {
      setDateExport('')
      setDateFrom('')
      setDateTo('')
      handleClose()
    }}
    disableSubmit={isSubmitDisabled()}
    handleSubmit={() => handleSubmit()}
  />
}
export default Export